import React, { useState, useEffect, useMemo } from 'react'
import { UserView, useGetUsersByTeamIdQuery } from '../../api/client'
import { formatDistanceToNow } from 'date-fns'
import { UserIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { getInLocalTime } from '../../util/timeHelpers'
import SearchInput from '../SearchInput'
import Spinner from '../Spinner'
import clsx from 'clsx'

interface TeamUsersTableProps {
    teamId: number
    isAdmin: boolean
}

function TeamUsersTable({ teamId, isAdmin }: TeamUsersTableProps) {
    const [keyword, setKeyword] = useState('')
    const [filteredUsers, setFilteredUsers] = useState<UserView[]>([])

    // Fetch users for this team
    const {
        data: usersData,
        loading: usersLoading,
        refetch: refetchUsers,
        error: usersError
    } = useGetUsersByTeamIdQuery({
        variables: { teamId },
        fetchPolicy: 'cache-and-network'
    })

    const users = useMemo(() => usersData?.getUsersByTeamId || [], [usersData])

    // Filter users based on search
    useEffect(() => {
        let filtered = users

        if (keyword.trim()) {
            filtered = filtered.filter((user) =>
                user.username.toLowerCase().includes(keyword.toLowerCase())
            )
        }

        setFilteredUsers(filtered)
    }, [users, keyword])

    const handleSearch = (searchTerm: string) => {
        setKeyword(searchTerm)
    }

    const handleRefresh = () => {
        refetchUsers()
    }

    if (usersError) {
        return (
            <div className="rounded-md bg-arwen-50 p-4">
                <div className="text-sm text-arwen-800">
                    Error loading users: {usersError.message}
                </div>
            </div>
        )
    }

    return (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <div>
                        {/* Jost 500 override of the global Merriweather h1-h4 rule in index.css */}
                        <h3 className="font-sans !font-medium text-lg text-gray-900">
                            Team members
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            {filteredUsers.length} member{filteredUsers.length !== 1 ? 's' : ''}
                            {filteredUsers.length !== users.length && ` of ${users.length} total`}
                        </p>
                    </div>

                    {/* Quick Stats */}
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                            <UserIcon className="h-4 w-4 mr-1" />
                            {users.length} total
                        </div>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-3">
                    <div className="flex-1">
                        <SearchInput
                            keyword={keyword}
                            onKeywordChange={setKeyword}
                            onSearch={handleSearch}
                        />
                    </div>
                    <button
                        onClick={handleRefresh}
                        disabled={usersLoading}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-arwen-500 disabled:opacity-50"
                    >
                        <MagnifyingGlassIcon
                            className={clsx('h-4 w-4', usersLoading && 'animate-spin')}
                        />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="overflow-hidden">
                {usersLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Spinner size={6} />
                    </div>
                ) : filteredUsers.length === 0 ? (
                    <div className="text-center py-12">
                        <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-2 text-sm text-gray-500">
                            {keyword
                                ? 'No users match your search criteria.'
                                : 'No users assigned to this team yet.'}
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        {/* Table chrome matches the Organisations list (OrgListPage.tsx) */}
                        <table className="min-w-full border-collapse text-sm">
                            <thead className="bg-gray-50/60">
                                <tr className="border-b border-gray-100">
                                    <th
                                        scope="col"
                                        className="px-6 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                                    >
                                        User
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                                    >
                                        Permissions
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                                    >
                                        Joined
                                    </th>
                                    {isAdmin && (
                                        <th
                                            scope="col"
                                            className="px-6 py-2.5 text-center text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                                        >
                                            Actions
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-50">
                                {filteredUsers.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-50/60">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-8 w-8">
                                                    <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                                        <span className="text-sm font-medium text-gray-800">
                                                            {user.username.charAt(0).toUpperCase()}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="ml-3">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {user.username}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        ID: {user.id}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {/* Neutral chips — the design system keeps red as the
                                                only saturated colour in UI chrome, so only the
                                                Admin permission carries the brand-red wash */}
                                            <div className="flex flex-wrap gap-1">
                                                {user.canReplyToContent && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                                                        Reply
                                                    </span>
                                                )}
                                                {user.canLikeContent && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                                                        Like
                                                    </span>
                                                )}
                                                {user.canUseReadStatus && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                                                        Read status
                                                    </span>
                                                )}
                                                {user.feedsEnabled && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                                                        Feeds
                                                    </span>
                                                )}
                                                {user.dashboardEnabled && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                                                        Reports
                                                    </span>
                                                )}
                                                {user.admin && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-arwen-50 text-arwen-800 border border-arwen-200">
                                                        Admin
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {user.createdOn
                                                ? formatDistanceToNow(
                                                      getInLocalTime(user.createdOn),
                                                      { addSuffix: true }
                                                  )
                                                : 'Unknown'}
                                        </td>
                                        {isAdmin && (
                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                <a
                                                    href={`#/admin/user/edit/${user.id}`}
                                                    className="inline-flex items-center rounded-md border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 shadow-sm hover:border-ink-300 hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-arwen-500"
                                                >
                                                    View
                                                </a>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    )
}

export default TeamUsersTable
